import { TinyColor } from '@ctrl/tinycolor'
import { intervalToDuration } from 'date-fns'
import sortBy from 'lodash/sortBy'
import React from 'react'
import {
  AbsoluteFill,
  Easing,
  interpolate,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion'
import { FallbackImg } from '../components/FallbackImg'
import {
  ResponsiveSceneProvider,
  useResponsiveSceneProps,
} from '../components/ResponsiveSceneContext'
import { SceneProps, Speaker } from '../types'

export function SpeakingScene({ audiogram, orientation, scene }: SceneProps) {
  return (
    <ResponsiveSceneProvider
      audiogram={audiogram}
      scene={scene}
      orientation={orientation}
    >
      <Background />

      <Speakers
        speakers={
          sortBy(audiogram.speakers, (s) => s.speakingTime)
            .reverse()
            .slice(0, 8) ?? []
        }
      />

      <StarLine />
      <TitleText text={TITLE_TEXT} />
    </ResponsiveSceneProvider>
  )
}

const TITLE_TEXT = `Who's talking?`

const Background = () => {
  const { audiogram } = useResponsiveSceneProps()

  return (
    <AbsoluteFill style={{ backgroundColor: audiogram.theme.colors.primary }} />
  )
}

const StarSVG = ({
  className,
  style,
}: {
  className?: string
  style?: React.CSSProperties
}) => {
  return (
    <svg
      className={className}
      style={style}
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M-1.909e-06 22.2014C19.5136 20.9333 20.5304 19.9164 21.7986 0.402826C23.0667 19.9164 24.0835 20.9333 43.5972 22.2014C24.0835 23.4696 23.0667 24.4864 21.7986 44C20.5304 24.4864 19.5136 23.4696 -1.909e-06 22.2014Z"
        fill="currentColor"
      />
    </svg>
  )
}

const StarLine = () => {
  const { audiogram, u, w, h } = useResponsiveSceneProps()
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()

  const time = frame / fps

  const rate = 0.25 * 360 * time

  const enterDriver = interpolate(time, [0, 1], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: Easing.out(Easing.exp),
  })

  const x = enterDriver * (w() - u(2))

  return (
    <>
      <div
        className="absolute -translate-y-1/2"
        style={{
          backgroundColor: audiogram.theme.colors.secondary,
          top: u(6),
          width: x,
          height: u(0.125),
        }}
      />
      <StarSVG
        className="absolute"
        style={{
          color: audiogram.theme.colors.secondary,
          top: u(6),
          width: u(3),
          height: u(3),
          transform: `translate(-50%,-50%) translateX(${x}px) rotate(${-rate}deg)`,
        }}
      />
    </>
  )
}

const TitleText = ({ text }: { text: string }) => {
  const { audiogram, u, w, h, orientation } = useResponsiveSceneProps()

  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()

  const time = frame / fps

  const enterDriver = interpolate(time, [0, 0.5], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })

  const opacity = enterDriver
  const x = interpolate(enterDriver, [0, 1], [-u(2), 0], {
    easing: Easing.out(Easing.exp),
  })

  return (
    <div
      className="absolute flex flex-row items-center overflow-hidden text-6xl"
      style={{
        color: audiogram.theme.colors.secondary,
        right: u(2),
        height: u(5),
        left: u(1.5),
        top: u(0.5),
        fontFamily: audiogram.theme.fonts.primary,
        lineHeight: 1.2,
        opacity,
        transform: `translateX(${x}px)`,
      }}
    >
      Who's talking?
    </div>
  )
}

const formatSpeakerTime = (seconds: number) => {
  const duration = intervalToDuration({ start: 0, end: seconds * 1000 })
  const hours = duration.hours
  const minutes = duration.minutes
  const remainingSeconds = duration.seconds

  let result = ''
  if (typeof hours !== 'undefined' && hours > 0) {
    result += `${hours} h `
  }
  if (typeof minutes !== 'undefined' && minutes > 0) {
    result += `${minutes} min `
  }
  if (typeof remainingSeconds !== 'undefined' && remainingSeconds > 0) {
    result += `${remainingSeconds} sec`
  }
  if (result === '') {
    result = '0 sec'
  }

  return result.trim()
}

const Speakers = ({ speakers }: { speakers: Speaker[] }) => {
  const { audiogram, u, w, h, orientation } = useResponsiveSceneProps()
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const time = frame / fps

  const sortedSpeakers = sortBy(speakers, (s) => s.speakingTime).reverse()
  const maxSpeakingTime = sortedSpeakers.at(0)?.speakingTime ?? 0

  const barSizing = {
    landscape: {
      paddingLeft: u(1),
      borderRightWidth: u(0.25),
    },
    portrait: {
      paddingLeft: u(1),
      borderRightWidth: u(0.25),
    },
    square: {
      paddingLeft: u(1),
      borderRightWidth: u(0.25),
    },
  }[orientation]

  const avatarImageSizing = {
    landscape: {
      width: u(2),
      height: u(2),
    },
    portrait: {
      width: u(2.5),
      height: u(2.5),
    },
    square: {
      width: u(2),
      height: u(2),
    },
  }[orientation]

  const topTextStyles = {
    landscape: {
      fontSize: u(1.25),
      lineHeight: 1,
    },
    portrait: {
      fontSize: u(2),
      lineHeight: `${u(3)}px`,
    },
    square: {
      fontSize: u(1.25),
      lineHeight: 1,
    },
  }[orientation]

  const bottomTextStyles = {
    landscape: {
      fontSize: u(0.8),
      lineHeight: 1,
      marginTop: u(0.3),
    },
    portrait: {
      fontSize: u(1.5),
      lineHeight: `${u(3)}px`,
    },
    square: {
      fontSize: u(0.8),
      lineHeight: 1,
      marginTop: u(0.3),
    },
  }[orientation]

  const widthFactor = {
    landscape: 0.1,
    portrait: 0.4,
    square: 0.25,
  }[orientation]

  const opacity = (t: number) =>
    interpolate(t, [0, 0.25], [0, 1], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    })

  const item = (t: number, finalWidth: number) => ({
    opacity: opacity(t),
    width:
      interpolate(finalWidth, [0, 1], [widthFactor, 1]) *
      w(
        interpolate(t, [0, 0.5], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
          easing: Easing.out(Easing.quad),
        }),
      ),
  })

  const accent = new TinyColor(audiogram.theme.colors.accent)

  return (
    <div
      className="absolute flex flex-col items-start"
      style={{
        top: u(6),
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      {speakers.map((speaker, index) => {
        const backgroundColor = accent.isLight()
          ? accent.darken(index * 5)
          : accent.lighten(index * 5)
        const color = backgroundColor.isLight() ? 'black' : 'white'
        const formattedSpeakingTime = formatSpeakerTime(
          speaker.speakingTime ?? 0,
        )
        const topText = speaker.name ?? formattedSpeakingTime
        const bottomText = speaker.name ? formattedSpeakingTime : undefined

        return (
          <React.Fragment key={`speaker:${index}:${speaker.speakingTime ?? 0}`}>
            <div
              style={{
                ...barSizing,
                height: '12.5%',
                overflow: 'hidden',
                backgroundColor: backgroundColor.toHexString(),
                borderRightColor: backgroundColor.darken(10).toHexString(),
                borderRightStyle: 'solid',
                color: color,
                ...item(
                  time - 0.1 * (index + 1),
                  (speaker.speakingTime ?? 0) / maxSpeakingTime,
                ),
              }}
            >
              <div className="flex h-full flex-row items-center">
                <div className="flex-shrink-0">
                  <FallbackImg
                    className="rounded-full"
                    style={{ left: 0, top: 0, ...avatarImageSizing }}
                    src={speaker.avatarUrl}
                  />
                </div>

                <div
                  className="flex flex-col overflow-hidden"
                  style={{
                    paddingLeft: u(1),
                    fontFamily: audiogram.theme.fonts.primary,
                  }}
                >
                  <div
                    style={topTextStyles}
                    className="overflow-hidden font-bold overflow-ellipsis whitespace-nowrap"
                  >
                    {topText}
                  </div>

                  {!!bottomText && (
                    <div
                      style={bottomTextStyles}
                      className="overflow-hidden overflow-ellipsis whitespace-nowrap"
                    >
                      {bottomText}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </React.Fragment>
        )
      })}
    </div>
  )
}
